/**
 * ReviewAndSendShell — shared layout for all review-and-send surfaces.
 *
 * PT-34 (2026-07-27): extracted from QuoteReviewSend.js. Consumed by:
 *   - QuoteReviewSend (quote review + send-for-signature)
 *   - InvoiceReviewSend (invoice review + send)
 *   - ChangeOrderReviewSend (PT-35, future)
 *
 * The shell owns:
 *   - 2-column layout (3/5 preview + 2/5 controls)
 *   - Header (DetailPageHeader)
 *   - Recipients card (multi-select checkboxes)
 *   - Display toggles card (role="switch" toggles, PT-46 46.4a)
 *   - Notes card
 *   - Footer (Cancel / Download PDF / Send)
 *
 * The shell does NOT own:
 *   - Data loading (consumer's useEffect)
 *   - Document projection (consumer's useMemo)
 *   - Internal summary (consumer-specific)
 *   - Extra rail cards (T&C picker, locked-terms, etc.)
 *   - Send action implementation (consumer's handleSend)
 *
 * Prop-driven; no shell-level logic that duplicates into consumers (34.4).
 *
 * Variant map (PT-46 46.4f):
 *   | Consumer              | displayToggles | recipients seeding | notes | extraRailCards |
 *   |-----------------------|----------------|-------------------|-------|----------------|
 *   | QuoteReviewSend       | 6 toggles      | customer contacts | yes   | T&C picker     |
 *   | ChangeOrderReviewSend | 3 toggles      | job’s customer     | yes   | —              |
 *   | InvoiceReviewSend     | 4 toggles      | customer contacts | yes   | —              |
 */

import React, { useState, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import DetailPageHeader from '../ui/DetailPageHeader';
import { BriefcaseIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../ui/Spinner';

export default function ReviewAndSendShell({
  // Identity / Navigation
  title,
  subtitle,
  backTo,
  backLabel = 'Back',
  status,
  // IDI-556: optional status pill label/tone override (change orders).
  statusLabel,
  statusTone,
  loading = false,

  // IDI-37 / IDI-464: per-surface delivery copy. Each surface states what its
  // send actually does (Quote and Change Order both email the link now). No
  // default: the old "no email yet" default was removed with IDI-464, and a
  // surface that passes nothing shows no note rather than a false one.
  deliveryNote,

  // Preview (injected renderer)
  children,

  // Recipients
  recipients = [],
  onToggleRecipient,
  onAddRecipient,

  // Display toggles
  displayToggles = [],
  displayOptions = {},
  onToggleDisplay,

  // Notes
  notes = '',
  onNotesChange,

  // Extra rail cards (consumer-specific: T&C picker, internal summary, etc.)
  extraRailCards,

  // Footer actions
  onCancel,
  onDownload,
  onSend,
  // IDI-133 completion (2026-09-01, Portica #40): header actions slot gains
  // Convert to Job. Gate on canConvertToJob; navigate to /quotes/:id/record-decision.
  // onMarkSent prop deleted (temporarily retained for backward-compat).
  onConvertToJob,
  canConvertToJob = false,
  sending = false,
  sendDisabled = false,
  sendLabel = 'Send',
  sendingLabel = 'Sending\u2026',

  // IDI-132: a sent or signed document is LOCKED — read-only, not hidden.
  // Recipients, display toggles, notes and the extra rail cards are disabled;
  // Download still works. `lockedLabel` ("Sent — locked" / "Signed — locked")
  // replaces the sent banner; `lockedMessage` explains. A consumer that still
  // allows a send on a locked document (Resend on a sent quote) passes onSend;
  // one that doesn't (signed: IDI-549) passes onSend undefined and
  // `sendBlockedReason`.
  locked = false,
  lockedLabel,
  lockedMessage,
  sendBlockedReason,
}) {
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const handleAddRecipient = useCallback(() => {
    if (!newEmail.trim() || !newEmail.includes('@')) return;
    onAddRecipient?.(newEmail.trim());
    setNewEmail('');
    setShowAddRecipient(false);
  }, [newEmail, onAddRecipient]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {/* Content column — `space-y-6` provides inter-block vertical rhythm for
          header, banner, two-column layout, and delivery notice.

          The sticky footer BELOW is deliberately a SIBLING of this container,
          NOT a child. Tailwind's `space-y-*` writes
            `margin-bottom: calc(gap * var(--tw-space-y-reverse))` → 0px
          on adjacent children at specificity (0,3,0), which DEFEATS a child's
          own `.-mb-4 md:-mb-6` at (0,1,0)/(0,2,0). The `-mb-*` class is
          written; the cascade drops it; the sticky bar's static position
          re-inherits the shell wrapper's `pb-4 md:pb-6` as a visible 24px gap.
          See CALIBRATION_LOG §C-10 for the cascade case study. R18's new
          `sticky-footer-space-y-parent` S2 rule guards against this pattern. */}
      <div className="space-y-6">
      {/* Header */}
      <DetailPageHeader
        title={title}
        subtitle={subtitle}
        backTo={backTo}
        backLabel={backLabel}
        status={status}
        statusLabel={statusLabel}
        statusTone={statusTone}
        actions={canConvertToJob && onConvertToJob && (
          <Button variant="secondary" onClick={onConvertToJob}>
            <BriefcaseIcon className="h-4 w-4 mr-1.5" />
            Convert to Job
          </Button>
        )}
      />

      {/* Info banner — W3.3 (2026-08-04): user must know at a glance whether
         this quote has been sent. Status-aware copy per operator ruling. */}
      <div
        className="px-4 py-3 text-sm rounded-md flex items-center gap-2"
        style={{ backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info-text, var(--color-text-default))' }}
        data-testid="rs-status-banner"
      >
        {locked ? (
          <>
            <span className="font-medium" data-testid="rs-locked-label">{lockedLabel || 'Locked'}</span>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {lockedMessage || '— this document has been delivered to the customer and is read-only.'}
            </span>
          </>
        ) : (<>
        <span className="font-medium">
          {status === 'sent' || status === 'signed' || status === 'acknowledged'
            ? 'Sent'
            : 'Not yet sent'}
        </span>
        <span style={{ color: 'var(--color-text-muted)' }}>
          {status === 'sent' || status === 'signed' || status === 'acknowledged'
            ? '— this document has been delivered to the customer.'
            : '— this is a preview of what your customer will receive.'}
        </span>
        </>)}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Preview (3/5) */}
        {/* NB: no outer <Card> wrapper — <QuoteDocument> paints its own document */}
        {/* surface via .qd-doc (background + border). Nesting a Card here would */}
        {/* stack two backgrounds + two borders (the R&S double-background bug). */}
        <div className="lg:col-span-3">
          {children}
        </div>

        {/* Right: Controls (2/5) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Recipients */}
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
              Recipients
            </h4>
            <div className="space-y-2">
              {recipients.map((r) => (
                <label key={r.id || r.email} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={r.checked}
                    disabled={locked}
                    onChange={() => onToggleRecipient?.(r.id || r.email)}
                  />
                  <span style={{ color: 'var(--color-text-default)' }}>
                    {r.name ? `${r.name} \u2014 ${r.email}` : r.email}
                  </span>
                </label>
              ))}
              {recipients.length === 0 && (
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  No recipients. Add at least one email address.
                </p>
              )}
            </div>
            {locked ? null : showAddRecipient ? (
              <div className="mt-3 flex gap-2">
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()}
                  className="flex-1"
                />
                <Button size="sm" onClick={handleAddRecipient}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddRecipient(false)}>Cancel</Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddRecipient(true)}
                className="mt-3 text-sm font-medium"
                style={{ color: 'var(--color-action-primary)' }}
              >
                + Add recipient
              </button>
            )}
          </Card>

          {/* Display Toggles */}
          {displayToggles.length > 0 && (
            <Card className="p-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                Display options
              </h4>
              <div className="space-y-3">
                {displayToggles.map((toggle) => (
                  <label key={toggle.id} className="flex items-center justify-between text-sm cursor-pointer">
                    <span style={{ color: 'var(--color-text-default)' }}>{toggle.label}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!displayOptions[toggle.id]}
                      disabled={locked}
                      onClick={() => { if (!locked) onToggleDisplay?.(toggle.id); }}
                      className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ backgroundColor: displayOptions[toggle.id] ? 'var(--color-action-primary)' : 'var(--color-border)' }}
                    >
                      <span
                        className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                        style={{ transform: displayOptions[toggle.id] ? 'translateX(16px)' : 'translateX(0)' }}
                      />
                    </button>
                  </label>
                ))}
              </div>
            </Card>
          )}

          {/* Consumer-specific extra cards (T&C picker, etc.) — rendered BEFORE Notes
             per W3.1 sidebar order: T&C · Attachments (future) · Notes */}
          {locked && extraRailCards ? (
            <div inert="" aria-disabled="true" className="opacity-75" data-testid="rs-locked-rail">{extraRailCards}</div>
          ) : extraRailCards}

          {/* Notes */}
          {onNotesChange && (
            <Card className="p-4">
              <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Notes
              </h4>
              <textarea
                value={notes}
                readOnly={locked}
                disabled={locked}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder={'Add a note for the customer…'}
                className="w-full text-sm border rounded-md p-2 resize-none"
                style={{ borderColor: 'var(--color-border)', minHeight: '80px' }}
                rows={3}
              />
            </Card>
          )}
        </div>
      </div>

      {/* PT-49 / IDI-37: the delivery note. IDI-464 removed the "no email
          yet" default (Change Orders email the link now); each surface passes
          its own accurate note, and none is shown without one. */}
      {deliveryNote && (
        <div
          className="mx-6 mb-2 rounded-md p-3 text-xs"
          style={{
            backgroundColor: 'var(--color-surface-sunken)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <strong>Note:</strong>{' '}{deliveryNote}
        </div>
      )}
      </div>
      {/* END space-y-6 content column — footer below is a SIBLING, not a child. */}

      {/* Footer — sibling of the space-y-6 content column above.

          Three bleed classes solve three distinct problems:
            • `-mx-4 md:-mx-6`  — horizontal: margin bleeds past the wrapper's
              max-width and MAIN's `px-*` on both edges. Plain box geometry.
            • `-mb-4 md:-mb-6`  — static-position: negative bottom margin on
              the sticky element itself expands ITS OWN constraint rectangle,
              allowing 24px more travel before flow clamps it. At specificity
              (0,1,0)/(0,2,0), NOT beaten by any ancestor selector — because
              this element is no longer a child of `space-y-6` (which would
              override at (0,3,0)). Same class shape as before; different
              cascade because the parent changed.
            • `mt-6`             — replaces the vertical rhythm that
              `space-y-6` was contributing above (1.5rem = 24px = design gap).

          Pinned position is solved at the LAYOUT level: LayoutNew's <main>
          has no `pb-*` (moved to the inner wrapper), so MAIN's padding-box
          bottom == viewport bottom and `sticky bottom-0` pins flush.

          Verification discipline (SKILL.md §Self-Validation):
            • Hard-reload the tab before probing — long-lived SPA tabs can
              hold stale CSS parses in memory even when the URL is correct.
            • Never validate a className fix via `element.style` injection.
              Inline styles bypass the cascade; classNames don't.
            • Sample scrollTop=0 AND scrollMax — sticky has two rest positions.

          See CALIBRATION_LOG §C-7 (rule AM-1), §C-9 (pinned/static duality),
          §C-10 (space-y-6 cascade specificity). */}
      <div
        className="sticky bottom-0 mt-6 -mx-4 md:-mx-6 -mb-4 md:-mb-6 flex items-center justify-end gap-3 px-6 py-4 border-t"
        style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
      >
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        {onDownload && (
          <Button variant="secondary" onClick={onDownload}>Download</Button>
        )}
        {/* IDI-133 completion (2026-09-01): Convert to Job now sits in header
            actions slot (replaces retired Mark Sent CTA). See above. */}
        {onSend ? (
          <Button
            onClick={onSend}
            disabled={sendDisabled || sending}
          >
            {sending ? sendingLabel : sendLabel}
          </Button>
        ) : sendBlockedReason ? (
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="rs-send-blocked">
            {sendBlockedReason}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * seedRecipientsFromContacts — shared seeding logic for all R&S surfaces.
 *
 * PT-46 46.4c: ensures consistent recipient seeding across Quote, CO, and Invoice.
 * Accepts a contacts array (from customer or job) and returns a recipients array
 * ready to pass to the shell.
 *
 * @param {Array} contacts - Array of { id, email, name, is_primary/isPrimary }
 * @param {object} [fallback] - Optional { email, name } for when contacts is empty
 * @returns {Array} recipients - Array of { id, email, name, checked }
 */
export function seedRecipientsFromContacts(contacts = [], fallback = null) {
  // IDI-483 (scope v2, Q-17): one row per email. Duplicate contact rows
  // sharing an email (pre-#495 copies, IDI-498) used to show 2-3 times, and
  // id-less contacts all became `contact-${email}`, so ticking one ticked its
  // twins. Keep the PRIMARY contact's row for an email; if none of them is
  // primary, the first in contact order. Contacts without an email can't
  // collide and are all kept.
  const list = (Array.isArray(contacts) ? contacts : []).filter((c) => c && (c.email || c.id));
  const byEmail = new Map(); // lower-cased email -> index into `kept`
  const kept = [];
  list.forEach((c, i) => {
    const key = String(c.email || '').trim().toLowerCase();
    const isPrimary = !!(c.isPrimary || c.is_primary);
    if (!key) {
      kept.push({ c, i });
      return;
    }
    if (!byEmail.has(key)) {
      byEmail.set(key, kept.length);
      kept.push({ c, i });
    } else if (isPrimary && !(kept[byEmail.get(key)].c.isPrimary || kept[byEmail.get(key)].c.is_primary)) {
      kept[byEmail.get(key)] = { c, i }; // the primary row wins its email
    }
  });

  const usedIds = new Set();
  const seeded = kept.map(({ c, i }) => {
    // A key unique per ROW: the contact's id when it has one (and isn't
    // already taken), else a positional key. Never a key shared by rows.
    let id = c.id ? String(c.id) : `contact-${i}`;
    if (usedIds.has(id)) id = `contact-${i}`;
    usedIds.add(id);
    return {
      id,
      email: c.email || '',
      name: c.name || '',
      checked: !!(c.isPrimary || c.is_primary),
    };
  });

  // If nothing was flagged primary, pre-check the first one so we
  // never open the composer with zero recipients selected.
  if (seeded.length > 0 && !seeded.some((r) => r.checked)) {
    seeded[0] = { ...seeded[0], checked: true };
  }

  if (seeded.length > 0) return seeded;

  // Fallback: single entry from customer_email
  if (fallback && fallback.email) {
    return [{ id: 'primary', email: fallback.email, name: fallback.name || '', checked: true }];
  }

  return [];
}
